import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'
import { useMsal } from '@azure/msal-react'
import { getRedirectUriDiagnostics } from '../auth/redirectUri'
import { useAuth } from '../context/AuthContext'
import { passwordLogin, getMyProfile, getMyProfileWithToken } from '../services/api'
import './Login.css'

const logoSrc = new URL('../Logo.png', import.meta.url).href

const SSO_PENDING_KEY = 'mimgrc_sso_pending'
const SSO_WAIT_MESSAGE =
  'Please wait whilst we check your credentials with Microsoft.'

function hasOAuthRedirectHash(): boolean {
  const h = window.location.hash || ''
  return (
    h.includes('code=') ||
    h.includes('id_token=') ||
    h.includes('access_token=') ||
    h.includes('error=')
  )
}

function LoginBrandedHeader() {
  return (
    <header className="login-card__banner">
      <img src={logoSrc} alt="Kingspan" className="login-card__logo" />
      <div className="login-card__divider" aria-hidden="true" />
      <div className="login-card__titles">
        <h1 className="login-card__title">MIM GRC Checker</h1>
        <p className="login-card__subtitle">Marketing Compliance Tool</p>
      </div>
    </header>
  )
}

export default function Login() {
  const { login, isAuthenticated } = useAuth()
  const { instance } = useMsal()
  const navigate = useNavigate()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showDevLogin, setShowDevLogin] = useState(false)
  const [ssoCompleting, setSsoCompleting] = useState(() => {
    if (sessionStorage.getItem(SSO_PENDING_KEY) === 'true') return true
    return hasOAuthRedirectHash()
  })

  const resolveApiScope = () => {
    const explicitScope = import.meta.env.VITE_AZURE_API_SCOPE?.trim()
    if (explicitScope) return explicitScope

    const apiClientId = import.meta.env.VITE_AZURE_API_CLIENT_ID?.trim()
    if (apiClientId) return `api://${apiClientId}/.default`

    return undefined
  }

  const apiScope = resolveApiScope()
  const redirectDiagnostics = getRedirectUriDiagnostics()
  const redirectTarget = (() => {
    try {
      return new URL(redirectDiagnostics.resolvedRedirectUri).origin
    } catch {
      return ''
    }
  })()
  const originMismatchWarning =
    !redirectDiagnostics.isUsingFallback &&
    redirectTarget &&
    redirectTarget !== window.location.origin
      ? `SSO redirects to ${redirectDiagnostics.resolvedRedirectUri}, but you are currently on ${window.location.origin}. Ensure both domains are added as SPA redirect URIs in Entra ID (for example calm-rock-0b0cc6e03.6.azurestaticapps.net and mim.kingspan.app).`
      : ''

  const scopes = apiScope ? [apiScope] : []

  // After Entra ID redirects back to the app, MSAL has tokens in its cache,
  // but our app-level auth state (and axios interceptor) only trust the token
  // stored in localStorage. On first load, bridge MSAL → our AuthContext.
  useEffect(() => {
    let cancelled = false
    const finishSsoUi = () => {
      if (!cancelled) setSsoCompleting(false)
    }

    if (!apiScope) {
      sessionStorage.removeItem(SSO_PENDING_KEY)
      setError('SSO is not fully configured: set VITE_AZURE_API_CLIENT_ID or VITE_AZURE_API_SCOPE for API access.')
      finishSsoUi()
      return () => {
        cancelled = true
      }
    }

    // If the user explicitly logged out of SSO in this tab, do not
    // auto-bootstrap from cached MSAL state until they click the
    // "Sign in with Microsoft" button again.
    if (sessionStorage.getItem('mimgrc_sso_logout') === 'true') {
      sessionStorage.removeItem(SSO_PENDING_KEY)
      finishSsoUi()
      return () => {
        cancelled = true
      }
    }

    const bootstrapFromMsal = async () => {
      try {
        const initFn = (instance as any)?.initialize
        if (typeof initFn === 'function') {
          await initFn.call(instance)
        }
        if (cancelled) return

        await instance.handleRedirectPromise()

        const accounts = instance.getAllAccounts()
        if (!accounts.length) {
          sessionStorage.removeItem(SSO_PENDING_KEY)
          finishSsoUi()
          return
        }

        setSsoCompleting(true)

        const account = accounts[0]
        const result = await instance.acquireTokenSilent({
          account,
          scopes,
        })

        if (!result.accessToken) {
          sessionStorage.removeItem(SSO_PENDING_KEY)
          finishSsoUi()
          return
        }

        const profile = await getMyProfileWithToken(result.accessToken)
        if (cancelled) return

        sessionStorage.removeItem(SSO_PENDING_KEY)
        login(result.accessToken, {
          userId: profile.userId,
          displayName: profile.displayName,
          email: profile.email,
          role: profile.role,
          businessLevel: profile.businessLevel,
          functionalRoles: profile.functionalRoles,
          divisionId: profile.divisionId,
          subdivisionId: profile.subdivisionId,
          businessEntityIds: profile.businessEntityIds,
          divisionName: profile.divisionName,
          subdivisionName: profile.subdivisionName,
          businessEntityNames: profile.businessEntityNames,
          isAutoProvisioned: profile.isAutoProvisioned,
          isProfileComplete: profile.isProfileComplete,
          authMethod: 'sso',
          entraObjectId: profile.entraObjectId,
          entraTenantId: profile.entraTenantId,
          entraPreferredUsername: profile.entraPreferredUsername,
          entraName: profile.entraName,
          entraIdentityProvider: profile.entraIdentityProvider,
        })

        navigate('/', { replace: true })
      } catch (err) {
        console.warn('Failed to bootstrap auth from Entra ID redirect', err)
        sessionStorage.removeItem(SSO_PENDING_KEY)
        finishSsoUi()
        const message =
          typeof (err as any)?.message === 'string'
            ? (err as any).message
            : typeof (err as any)?.errorMessage === 'string'
              ? (err as any).errorMessage
              : ''
        if (message.includes('AADSTS500011') || message.includes('invalid_resource')) {
          setError('SSO configuration error (AADSTS500011): the API resource is not found in this tenant. Ask IT to verify API app registration and admin consent for the configured scope.')
        } else if (message) {
          setError(`Microsoft sign-in succeeded but API token validation failed: ${message}`)
        } else {
          setError('Microsoft sign-in succeeded but API token validation failed. Please contact support and include the exact time of failure.')
        }
      }
    }

    bootstrapFromMsal()
    return () => {
      cancelled = true
    }
  }, [instance, login, navigate, scopes])

  const handleEntraLogin = async () => {
    if (ssoCompleting) return
    setError('')
    sessionStorage.removeItem('mimgrc_sso_logout')
    if (!apiScope) {
      setError('SSO is not fully configured: set VITE_AZURE_API_CLIENT_ID or VITE_AZURE_API_SCOPE for API access.')
      return
    }
    try {
      sessionStorage.setItem(SSO_PENDING_KEY, 'true')
      await instance.loginRedirect({
        scopes,
      })
    } catch (err: any) {
      sessionStorage.removeItem(SSO_PENDING_KEY)
      console.error('Entra ID login failed', err)
      setError('Microsoft sign-in failed. Please try again.')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!identifier.trim() || !password) {
      setError('User ID or email and password are required.')
      return
    }

    setLoading(true)
    try {
      const res = await passwordLogin({ identifier: identifier.trim(), password })
      login(res.token, {
        userId: res.user.userId,
        displayName: res.user.displayName ?? '',
        email: res.user.email,
        role: res.user.role,
        businessLevel: res.user.businessLevel,
        functionalRoles: res.user.functionalRoles ?? [],
        divisionId: res.user.divisionId,
        subdivisionId: res.user.subdivisionId,
        businessEntityIds: res.user.businessEntityIds ?? [],
        authMethod: 'local',
      })
      try {
        const profile = await getMyProfile()
        login(res.token, {
          userId: profile.userId,
          displayName: profile.displayName,
          email: profile.email,
          role: profile.role,
          businessLevel: profile.businessLevel,
          functionalRoles: profile.functionalRoles,
          divisionId: profile.divisionId,
          subdivisionId: profile.subdivisionId,
          businessEntityIds: profile.businessEntityIds,
          divisionName: profile.divisionName,
          subdivisionName: profile.subdivisionName,
          businessEntityNames: profile.businessEntityNames,
          isAutoProvisioned: profile.isAutoProvisioned,
          isProfileComplete: profile.isProfileComplete,
          authMethod: 'local',
          entraObjectId: profile.entraObjectId,
          entraTenantId: profile.entraTenantId,
          entraPreferredUsername: profile.entraPreferredUsername,
          entraName: profile.entraName,
          entraIdentityProvider: profile.entraIdentityProvider,
        })
      } catch {
        // Non-fatal
      }
      navigate('/', { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Login failed.')
    } finally {
      setLoading(false)
    }
  }

  if (isAuthenticated) {
    navigate('/', { replace: true })
    return null
  }

  if (ssoCompleting) {
    return (
      <div className="login-page">
        <div className="login-card" role="status" aria-live="polite">
          <LoginBrandedHeader />
          <div className="login-card__sso-wait">
            <Spinner animation="border" className="mb-3" />
            <p className="fw-medium mb-2">{SSO_WAIT_MESSAGE}</p>
            <p className="text-muted small mb-0">
              This usually takes a few seconds. Do not close this page or click Sign in again.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const hasConfigAlerts =
    (redirectDiagnostics.isUsingFallback && redirectDiagnostics.warningMessage) ||
    !!originMismatchWarning

  return (
    <div className="login-page">
      <div className="login-card">
        <LoginBrandedHeader />

        <div className="login-card__body">
          <p className="login-card__welcome">
            Welcome to the digital MIM compliance checker app. Please click on the SSO button to log in
          </p>

          {(hasConfigAlerts || error) && (
            <div className="login-card__alerts">
              {redirectDiagnostics.isUsingFallback && redirectDiagnostics.warningMessage && (
                <Alert variant="warning" className="small mb-2">
                  {redirectDiagnostics.warningMessage}
                </Alert>
              )}
              {originMismatchWarning && (
                <Alert variant="warning" className="small mb-2">
                  {originMismatchWarning}
                </Alert>
              )}
              {error && (
                <Alert variant="danger" className="small mb-0">
                  {error}
                </Alert>
              )}
            </div>
          )}

          <button
            type="button"
            className="login-card__sso-btn"
            onClick={handleEntraLogin}
            disabled={ssoCompleting}
          >
            Microsoft Single Sign On (SSO)
          </button>

          {import.meta.env.DEV && (
            <div className="login-dev">
              <button
                type="button"
                className="login-dev-toggle"
                onClick={() => setShowDevLogin((prev) => !prev)}
              >
                {showDevLogin ? 'Hide developer sign-in' : 'Developer sign-in'}
              </button>
              {showDevLogin && (
                <Form onSubmit={handleSubmit} className="login-dev-form">
                  <Form.Group>
                    <Form.Label>User ID or Email</Form.Label>
                    <Form.Control
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      placeholder="Enter user ID or email"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={password ?? ''}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                    />
                  </Form.Group>
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading && <Spinner size="sm" animation="border" className="me-2" />}
                    {loading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </Form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
